import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, setUserInfo } from '../api/client';
import { translateError } from '../utils/errorMessages';

/**
 * Landing page for the cross-subdomain organization switch.
 *
 * Receives ONLY the new token in the URL and rebuilds the session from
 * `GET /api/users/session`. Nothing about the user travels in the URL: the
 * token already encodes the org, and the tenant is the subdomain we are on.
 *
 * The previous version passed the whole user object as base64 `data`, which
 * (a) crashed the switcher outright for any org whose name left Latin-1 —
 * `btoa` throws on e.g. "ZŠ Květná Praha" — and (b) let the URL dictate
 * `isSuperAdmin`/`memberships` for the stored session.
 */
const OrgSwitchCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setError(t('auth.orgSwitchNoToken'));
      return;
    }

    let cancelled = false;

    const hydrate = async () => {
      try {
        // Authenticate with the handed-off token explicitly: it is not in
        // localStorage yet, and whatever is there belongs to the previous org.
        const response = await api.auth.session(token);
        if (cancelled) return;

        const userData = response.data as any;
        setUserInfo({ ...userData, token });

        // Full navigation rather than a route change, so AuthContext and every
        // tenant-scoped query re-initialise against the new organization.
        window.location.href = '/admin/calendar';
      } catch (err: any) {
        if (cancelled) return;
        setError(translateError(err, t, 'common.error'));
      }
    };

    hydrate();

    return () => {
      cancelled = true;
    };
  }, [searchParams, t]);

  if (error) {
    return (
      <div className="page-content">
        <div className="form-container">
          <h2>{t('auth.orgSwitchFailed')}</h2>
          <p className="error-message">{error}</p>
          <p>{t('auth.orgSwitchRetry')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="form-container">
        <h2>{t('auth.orgSwitchInProgress')}</h2>
        <p>{t('auth.orgSwitchWait')}</p>
      </div>
    </div>
  );
};

export default OrgSwitchCallback;
